import DashboardLayout from '@/components/DashboardLayout';
import { router } from '@inertiajs/react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Package, Plus, Edit, Trash2, Repeat } from 'lucide-react';

interface LessonPackage {
  id: number;
  name: string;
  description: string;
  lessons_count: number;
  price: string;
  formatted_price: string;
  price_per_lesson: number;
  validity_days: number | null;
  active: boolean;
  generates_acquisition_lead: boolean;
  billing_cycle: string;
  duration_months: number | null;
  lessons_per_week: number | null;
  monthly_price: string | null;
}

const CYCLE_LABEL: Record<string, string> = {
  one_time: 'Avulso',
  monthly: 'Mensal',
  semestral: 'Semestral',
  annual: 'Anual',
};

interface Props {
  lesson_packages: LessonPackage[];
}

export default function Index({ lesson_packages }: Props) {
  const handleDelete = (packageId: number) => {
    if (confirm('Tem certeza que deseja excluir este pacote?')) {
      router.delete(`/lesson_packages/${packageId}`);
    }
  };

  return (
    <DashboardLayout>
    <div className="p-8 max-w-7xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div className="flex items-center gap-4">
          <div className="p-3 bg-primary/10 rounded-lg">
            <Package className="w-6 h-6 text-primary" />
          </div>
          <div>
            <h1 className="text-3xl font-bold">Pacotes de Aulas</h1>
            <p className="text-muted-foreground">
              {lesson_packages.length} {lesson_packages.length === 1 ? 'pacote cadastrado' : 'pacotes cadastrados'}
            </p>
          </div>
        </div>
        <Button onClick={() => router.visit('/lesson_packages/new')}>
          <Plus className="w-4 h-4 mr-2" />
          Novo Pacote
        </Button>
      </div>

      {/* Tabela de Pacotes */}
      <Card>
        <CardHeader>
          <CardTitle>Lista de Pacotes</CardTitle>
        </CardHeader>
        <CardContent>
          {lesson_packages.length === 0 ? (
            <div className="text-center py-12">
              <Package className="w-12 h-12 text-muted-foreground mx-auto mb-4 opacity-50" />
              <h3 className="text-lg font-semibold mb-2">Nenhum pacote cadastrado</h3>
              <p className="text-muted-foreground mb-4">
                Crie seu primeiro pacote de aulas para começar a vender
              </p>
              <Button onClick={() => router.visit('/lesson_packages/new')}>
                <Plus className="w-4 h-4 mr-2" />
                Criar Primeiro Pacote
              </Button>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Nome</TableHead>
                    <TableHead>Tipo</TableHead>
                    <TableHead className="text-center">Nº Aulas</TableHead>
                    <TableHead>Valor</TableHead>
                    <TableHead>Preço/Aula</TableHead>
                    <TableHead className="text-center">Validade</TableHead>
                    <TableHead className="text-center">Status</TableHead>
                    <TableHead className="text-right">Ações</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {lesson_packages.map((pkg) => {
                    const isRecurring = pkg.billing_cycle && pkg.billing_cycle !== 'one_time';
                    return (
                    <TableRow key={pkg.id}>
                      <TableCell className="font-medium">
                        <div className="flex items-center gap-2">
                          {pkg.name}
                          {pkg.generates_acquisition_lead && (
                            <Badge variant="outline" className="text-[10px] px-1.5 py-0 border-orange-300 text-orange-600 dark:border-orange-700 dark:text-orange-400">CRM</Badge>
                          )}
                        </div>
                        {pkg.description && (
                          <span className="text-xs text-muted-foreground line-clamp-1 mt-0.5 block">
                            {pkg.description}
                          </span>
                        )}
                      </TableCell>
                      <TableCell>
                        {isRecurring ? (
                          <Badge variant="outline" className="gap-1 border-blue-300 text-blue-700 dark:border-blue-700 dark:text-blue-400">
                            <Repeat className="w-3 h-3" />
                            {CYCLE_LABEL[pkg.billing_cycle]} {pkg.lessons_per_week}x/sem
                          </Badge>
                        ) : (
                          <Badge variant="outline">Avulso</Badge>
                        )}
                      </TableCell>
                      <TableCell className="text-center">
                        <Badge variant="secondary">{pkg.lessons_count}</Badge>
                      </TableCell>
                      <TableCell className="font-semibold text-green-700">
                        {isRecurring && pkg.monthly_price ? (
                          <div className="flex flex-col">
                            <span>{new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(parseFloat(pkg.monthly_price))}<span className="text-xs font-normal text-muted-foreground">/mês</span></span>
                            <span className="text-[10px] font-normal text-muted-foreground">
                              Total: {new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(parseFloat(pkg.price))}
                            </span>
                          </div>
                        ) : (
                          new Intl.NumberFormat('pt-BR', {
                            style: 'currency',
                            currency: 'BRL'
                          }).format(parseFloat(pkg.price))
                        )}
                      </TableCell>
                      <TableCell>
                        <span className="text-sm">
                          {new Intl.NumberFormat('pt-BR', {
                            style: 'currency',
                            currency: 'BRL'
                          }).format(pkg.price_per_lesson)}
                        </span>
                      </TableCell>
                      <TableCell className="text-center">
                        {pkg.validity_days ? (
                          <span className="text-sm">{pkg.validity_days} dias</span>
                        ) : (
                          <span className="text-xs text-muted-foreground">Sem validade</span>
                        )}
                      </TableCell>
                      <TableCell className="text-center">
                        <Badge variant={pkg.active ? 'default' : 'secondary'}>
                          {pkg.active ? 'Ativo' : 'Inativo'}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-2">
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => router.visit(`/lesson_packages/${pkg.id}/edit`)}
                          >
                            <Edit className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleDelete(pkg.id)}
                          >
                            <Trash2 className="w-4 h-4 text-destructive" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                  })}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
    </DashboardLayout>
  );
}
